import { postClientDiagnostic } from './clientDiagnostics'

type NotificationOpenSource = 'native' | 'service-worker' | 'pending' | 'direct'

type PendingNotificationOpen = {
  openId: string
  source: NotificationOpenSource
  chatId: string
  topicId: string | null
  messageId: string | null
  startMs: number
  prefetchMs?: number
  nativePlatform?: boolean
}

type StartNotificationOpenInput = {
  openId?: string | null
  source: NotificationOpenSource
  chatId: string
  topicId?: string | null
  messageId?: string | null
  startMs?: number | null
  prefetchMs?: number | null
  nativePlatform?: boolean | null
}

type FinishNotificationOpenInput = {
  chatId: string
  topicId?: string | null
  messageId?: string | null
  latestMessageId?: string | null
  messageCount: number
  targetMessagePresent?: boolean | null
  notificationOpen: boolean
  appVersion?: string | null
}

const pendingByOpenId = new Map<string, PendingNotificationOpen>()
const latestByScope = new Map<string, string>()

function nowMs(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now()
  }
  return Date.now()
}

function scopeKey(chatId: string, topicId?: string | null): string {
  return `${chatId}:${topicId ?? ''}`
}

function makeOpenId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

function currentBuildVersion(): string | null {
  try {
    if (typeof __BUILD_VERSION__ === 'string' && __BUILD_VERSION__) return __BUILD_VERSION__
  } catch {
    /* build global may not exist in test harnesses */
  }
  return null
}

export function startNotificationOpenTiming(input: StartNotificationOpenInput): string {
  const scope = scopeKey(input.chatId, input.topicId ?? null)
  const openId = input.openId || latestByScope.get(scope) || makeOpenId()
  const existing = pendingByOpenId.get(openId)
  const shouldRecordTap = !existing
  const entry: PendingNotificationOpen = {
    openId,
    source: existing?.source ?? input.source,
    chatId: input.chatId,
    topicId: input.topicId ?? null,
    messageId: input.messageId ?? existing?.messageId ?? null,
    startMs: input.startMs ?? existing?.startMs ?? nowMs(),
    prefetchMs: input.prefetchMs ?? existing?.prefetchMs,
    nativePlatform: input.nativePlatform ?? existing?.nativePlatform ?? undefined,
  }
  pendingByOpenId.set(openId, entry)
  latestByScope.set(scopeKey(entry.chatId, entry.topicId), openId)
  if (shouldRecordTap) {
    postClientDiagnostic('/api/client-diagnostics/notification-open', {
      event: 'tap',
      source: entry.source,
      open_id: openId,
      chat_id: entry.chatId,
      topic_id: entry.topicId,
      message_id: entry.messageId,
      native_platform: entry.nativePlatform,
      app_version: currentBuildVersion(),
    })
  }
  return openId
}

export function updateNotificationOpenTiming(
  openId: string | null | undefined,
  patch: { prefetchMs?: number | null },
): void {
  if (!openId) return
  const entry = pendingByOpenId.get(openId)
  if (!entry) return
  if (patch.prefetchMs != null) entry.prefetchMs = Math.round(patch.prefetchMs)
}

export function finishNotificationOpenTiming(input: FinishNotificationOpenInput): number | null {
  const scope = scopeKey(input.chatId, input.topicId ?? null)
  const openId = latestByScope.get(scope)
  if (!openId) return null
  const entry = pendingByOpenId.get(openId)
  if (!entry) return null
  const durationMs = Math.max(0, Math.round(nowMs() - entry.startMs))
  pendingByOpenId.delete(openId)
  latestByScope.delete(scope)
  postClientDiagnostic('/api/client-diagnostics/notification-open', {
    event: 'render',
    source: entry.source,
    open_id: openId,
    chat_id: input.chatId,
    topic_id: input.topicId ?? null,
    message_id: input.messageId ?? entry.messageId,
    latest_message_id: input.latestMessageId ?? null,
    duration_ms: durationMs,
    prefetch_ms: entry.prefetchMs,
    message_count: input.messageCount,
    target_message_present: input.targetMessagePresent ?? null,
    notification_open: input.notificationOpen,
    native_platform: entry.nativePlatform,
    app_version: input.appVersion ?? null,
  })
  return durationMs
}
